import { getColors } from "@/shared/utils/ganttColors";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	Box,
	Chip,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { CarrierChangeData } from "../../../models/ShiftReportResponse";
import {
	bodyCellSx,
	EllipsisCell,
	firstBodyCellSx,
	formatTons,
	groupDividerSx,
	groupHeaderCellSx,
	headerCellSx,
} from "./utils";

type CarrierSideCellsProps = {
	carrier?: CarrierChangeData;
	showUninstalledAt: boolean;
	showTons?: boolean;
};

const CellWithSubtext = ({
	text,
	subtext,
	sx,
	width,
}: {
	text: string;
	subtext?: string | null;
	sx?: any;
	width?: number;
}) => (
	<TableCell sx={{ fontSize: "0.8rem", ...sx }}>
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				width,
				minWidth: width,
				maxWidth: width,
			}}
		>
			<Box
				component="span"
				style={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					userSelect: "none",
				}}
			>
				{text}
			</Box>
			{subtext && (
				<Box
					component="span"
					style={{
						fontSize: "0.7rem",
						color: "#9e9e9e",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						userSelect: "none",
					}}
				>
					{subtext}
				</Box>
			)}
		</Box>
	</TableCell>
);

const CarrierSideCells = ({
	carrier,
	showUninstalledAt,
	showTons = false,
}: CarrierSideCellsProps) => {
	if (!carrier) {
		return (
			<>
				<TableCell sx={firstBodyCellSx}>-</TableCell>
				<TableCell sx={bodyCellSx}>-</TableCell>
				<TableCell sx={bodyCellSx}>-</TableCell>
				<TableCell sx={bodyCellSx}>-</TableCell>
				<TableCell sx={bodyCellSx}>-</TableCell>
				<TableCell sx={bodyCellSx}>-</TableCell>
			</>
		);
	}

	const sectionColor = getColors(carrier.SectionId!);
	const carrierSubtext =
		showTons && carrier.CarrierTons != null
			? `${formatTons(carrier.CarrierTons)} tons`
			: null;
	const passSubtext =
		showTons && carrier.SelectedPass != null && carrier.SelectedPassTons != null
			? `${formatTons(carrier.SelectedPassTons)} tons`
			: null;

	return (
		<>
			<CellWithSubtext
				text={carrier.CarrierName || "-"}
				subtext={carrierSubtext}
				sx={firstBodyCellSx}
				width={120}
			/>
			<EllipsisCell text={carrier.TopRollName || "-"} width={140} />
			<EllipsisCell text={carrier.BottomRollName || "-"} width={140} />
			<TableCell sx={bodyCellSx}>
				<Chip
					label={carrier.SectionName || "-"}
					sx={{
						backgroundColor: sectionColor.fill,
						border: `1px solid ${sectionColor.border}`,
						color: sectionColor.text,
						fontWeight: 600,
						fontSize: "0.75rem",
						height: "22px",
						"& .MuiChip-label": { px: 1 },
					}}
				/>
			</TableCell>
			<TableCell sx={bodyCellSx}>
				{dxtToLocalServerTime(
					showUninstalledAt ? carrier.UninstalledAt : carrier.InstalledAt,
					"MM/dd HH:mm",
				)}
			</TableCell>
			<CellWithSubtext
				text={carrier.SelectedPass != null ? String(carrier.SelectedPass) : "-"}
				subtext={passSubtext}
				width={80}
			/>
		</>
	);
};

type CarrierChangesTableProps = {
	uninstalledCarriers: CarrierChangeData[];
	installedCarriers: CarrierChangeData[];
};

type CarrierRow = {
	standName: string;
	standSortOrder: number;
	uninstalled?: CarrierChangeData;
	installed?: CarrierChangeData;
};

export const CarrierChangesTable = ({
	uninstalledCarriers,
	installedCarriers,
}: CarrierChangesTableProps) => {
	const rowsByStand = new Map<string, CarrierRow>();

	const upsert = (
		carrier: CarrierChangeData,
		side: "uninstalled" | "installed",
	) => {
		const key = carrier.StandName || String(carrier.StandSortOrder);
		const existing = rowsByStand.get(key) ?? {
			standName: carrier.StandName,
			standSortOrder: carrier.StandSortOrder,
		};
		existing[side] = carrier;
		rowsByStand.set(key, existing);
	};

	for (const c of uninstalledCarriers || []) upsert(c, "uninstalled");
	for (const c of installedCarriers || []) upsert(c, "installed");

	const rows = Array.from(rowsByStand.values()).sort(
		(a, b) => b.standSortOrder - a.standSortOrder,
	);

	if (rows.length === 0) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height={50}
				sx={{ backgroundColor: "#fafafa", borderRadius: 1 }}
			>
				<Typography color="text.secondary" variant="body2">
					No carrier changes
				</Typography>
			</Box>
		);
	}

	return (
		<TableContainer
			component={Paper}
			elevation={0}
			sx={{ overflow: "auto", border: "1px solid #e0e0e0" }}
		>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell rowSpan={2} sx={headerCellSx}>
							Stand
						</TableCell>
						<TableCell
							colSpan={6}
							sx={{ ...groupHeaderCellSx, color: "#e65100", ...groupDividerSx }}
						>
							Previous (Uninstalled)
						</TableCell>
						<TableCell
							colSpan={6}
							sx={{ ...groupHeaderCellSx, color: "#1565c0", ...groupDividerSx }}
						>
							New (Installed)
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ ...headerCellSx, ...groupDividerSx }}>
							Carrier
						</TableCell>
						<TableCell sx={headerCellSx}>Top Roll</TableCell>
						<TableCell sx={headerCellSx}>Bottom Roll</TableCell>
						<TableCell sx={headerCellSx}>Section</TableCell>
						<TableCell sx={headerCellSx}>Uninstalled At</TableCell>
						<TableCell sx={headerCellSx}>Pass</TableCell>
						<TableCell sx={{ ...headerCellSx, ...groupDividerSx }}>
							Carrier
						</TableCell>
						<TableCell sx={headerCellSx}>Top Roll</TableCell>
						<TableCell sx={headerCellSx}>Bottom Roll</TableCell>
						<TableCell sx={headerCellSx}>Section</TableCell>
						<TableCell sx={headerCellSx}>Installed At</TableCell>
						<TableCell sx={headerCellSx}>Pass</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.standName || row.standSortOrder} hover>
							<TableCell sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
								{row.standName || "-"}
							</TableCell>
							<CarrierSideCells
								carrier={row.uninstalled}
								showUninstalledAt
								showTons
							/>
							<CarrierSideCells
								carrier={row.installed}
								showUninstalledAt={false}
							/>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
